import { createApi } from "@reduxjs/toolkit/query/react";
import RtkBaseQueryWithReauth from "./RtkBaseQueryWithReauth";
import ApiPath from "./ConstantApi";

interface EmployeeListResponse {
  data: EmployeeData[];
  totalRecords: number;
  totalPages: number;
  currentPage: number;
}

interface EmployeeListParams {
  page: number;
  pageSize: number;
  search?: string;
  role?: string;
  status?: string;
  isActive?: boolean | string;
  seniorityLevel?: string;
  experienceMin?: string;
  experienceMax?: string;
}

interface EmployeeData {
  firstName: string;
  lastName: string;
  email: string;
  mobile: string;
  empId: string;
  roleId: string;
}

interface UpdateEmployeeRequest {
  id: string;
  data: EmployeeData;
}

export interface EmployeeProfileResponse {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  mobile: string;
  countryDialCode:string;
  empId: string;
  roleId:string;
  experience: string;
  seniorityLevel: string;
  employmentType: string;
  salary: string;
  isActive: boolean;
  /**
   * True while the set-password invite is still outstanding -- sent and
   * never used, whether it is valid or expired. The server works it out
   * from the reset token, which never leaves the server itself.
   */
  hasPendingInvite?: boolean;
  department: string;
  joiningDate: string;
  // permissions:any[];
  permissions:Record<string, Set<string>>;
  individualPermissions:Record<string, Set<string>>;
}

export const EmployeeService = createApi({
  reducerPath: "EmployeesApi",
  baseQuery: RtkBaseQueryWithReauth(process.env.REACT_APP_BASE_URL || ''),
  tagTypes: ["Employees"],
  keepUnusedDataFor: 300,
  endpoints: (builder) => ({
    getEmployees: builder.query<EmployeeListResponse, EmployeeListParams>({
      query: (params) => {
        const filters = [];
        
        // Only add isActive filter if it's specified and not empty string
        if (params.isActive !== "" && params.isActive !== undefined) {
          filters.push({ "key": "isActive", "value": params.isActive });
        }
        if(params.role !== "" && params.role !== undefined) {
          filters.push({ "key": "role", "value": params.role });
        }
        if(params.seniorityLevel !== "" && params.seniorityLevel !== undefined) {
          filters.push({ "key": "seniorityLevel", "value": params.seniorityLevel });
        }
        if(params.experienceMin !== "" && params.experienceMin !== undefined) {
          filters.push({ "key": "experienceMin", "value": params.experienceMin });
        }
        if(params.experienceMax !== "" && params.experienceMax !== undefined) {
          filters.push({ "key": "experienceMax", "value": params.experienceMax });
        }
        
        return {
          url: `${ApiPath.getAllEmployees}`,
          method: "POST",
          body: {
            "filters": filters,
            "sortBy": "createdAt",
            "sortOrder": "desc",
            "page": params.page,
            "pageSize": params.pageSize,
            "search": params.search,
          }
        };
      },
      providesTags: ["Employees"],
    }),

    createAndUpdateEmployee: builder.mutation<any, EmployeeData | UpdateEmployeeRequest>({
      query: (payload) => {
        // Check if this is an update request (has an id)
        if ('id' in payload) {
          return {
            url: `${ApiPath.updateEmployee}/${payload.id}`,
            method: 'PUT',
            body: payload.data,
          };
        }
        // This is a create request
        return {
          url: ApiPath.createEmployee,
          method: 'POST',
          body: payload,
        };
      },
      invalidatesTags: ["Employees"],
    }),

    getEmployeeById: builder.query<EmployeeProfileResponse, string>({
      query: (id) => ({
        url: `${ApiPath.getEmployee}/${id}`,
        method: "GET",
      }),
      transformResponse: (response: { data: EmployeeProfileResponse }) => response.data,
      providesTags: ["Employees"],
    }),

    getemployeecheckmobile: builder.query<any, { mobile: string; countryDialCode: string }>({
      query: ({ mobile, countryDialCode }) => ({
        url: `${ApiPath.employeecheckmobile}`,
        method: "GET",
        params: { mobile, countryDialCode },
      }),
      transformResponse: (response: { data: any }) => response.data,
      providesTags: ["Employees"],
    }),
    

    getAllCountry: builder.query({
      query: ({ ...patch }) => ({
        url: `${ApiPath.getAllCountry}`,
        method: 'POST',
        body: patch,
      }),
      providesTags: ["Employees"],
      // async onQueryStarted(arg, { dispatch, queryFulfilled }) {
      //   try {
      //     const { data }: any = await queryFulfilled; // Wait for the query to resolve
          
      //     // Create a new sorted array to avoid mutating the original response
      //     const sortedCountryList = [...data.data].sort((a: { name: { common: string } }, b: { name: { common: string } }) =>
      //       a.name.common.localeCompare(b.name.common)
      //     );

      //     // console.log(sortedCountryList, 'sortedCountryList');

      //     dispatch(setCounty({
      //       countryList: sortedCountryList
      //     }));
      //   } catch (error) {
      //     console.error('Error fetching user:', error);
      //   }
      // },
    }),

    /**
     * Send an employee their "set password" link again.
     *
     * The invite from creating an employee lasts 24 hours, and after that
     * there was no way to reissue it from the admin -- the employee had to
     * find "Forgot password" on the login page themselves.
     *
     * A new link replaces the old one, so any earlier invite stops
     * working. The message the server returns says so, and is shown to
     * whoever pressed the button.
     */
    resendEmployeeInvite: builder.mutation<
      { status: string; message: string },
      { employeeId: string }
    >({
      query: (body) => ({
        url: ApiPath.resendEmployeeInvite,
        method: "POST",
        body,
      }),
    }),

    // ... other endpoints remain same
  }),

  
});

export const {
  useGetEmployeesQuery,
  useLazyGetEmployeesQuery,
  useCreateAndUpdateEmployeeMutation,
  useLazyGetEmployeeByIdQuery,
  useLazyGetAllCountryQuery,
  useLazyGetemployeecheckmobileQuery,
  useResendEmployeeInviteMutation,
} = EmployeeService;